/**
 * Lightweight contextual SFX for Dragon Tale.
 *
 * Pure Web Audio API — no assets to load, no CDN calls. Every SFX is
 * synthesized on-the-fly from oscillators + noise + envelope shaping.
 * Total code footprint: ~4KB.
 *
 * Usage:
 *   import { playSfx } from "@/lib/sfx";
 *   playSfx("sword_clang");
 *   playSfx("boss_roar");
 *   playSfx("door_creak");
 *
 * Respects prefers-reduced-motion AND checks a localStorage flag
 * ("sfxMuted") so the existing music mute button can extend to SFX.
 */

let _ctx = null;
function ctx() {
  if (_ctx) return _ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  _ctx = new AC();
  return _ctx;
}

function muted() {
  try { return localStorage.getItem("sfxMuted") === "1"; } catch { return false; }
}

// Envelope helper — quick attack, exponential decay.
function env(gain, t0, attack, sustain, decay, peak) {
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(peak, t0 + attack);
  gain.gain.setValueAtTime(peak, t0 + attack + sustain);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + sustain + decay);
}

// -------- SFX voices --------

function swordClang(ac, t0) {
  // Metallic clang: high-freq noise burst + bandpass + rapid decay
  const buf = ac.createBuffer(1, ac.sampleRate * 0.4, ac.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ac.sampleRate * 0.06));
  const src = ac.createBufferSource(); src.buffer = buf;
  const bp = ac.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 3400; bp.Q.value = 6;
  const g = ac.createGain();
  env(g, t0, 0.002, 0.02, 0.35, 0.6);
  src.connect(bp).connect(g).connect(ac.destination);
  src.start(t0); src.stop(t0 + 0.42);
}

function bossRoar(ac, t0) {
  // Deep growling roar: low sawtooth with slow LFO, big reverb-ish decay
  const osc = ac.createOscillator(); osc.type = "sawtooth"; osc.frequency.value = 68;
  const lfo = ac.createOscillator(); lfo.type = "sine"; lfo.frequency.value = 6;
  const lfoGain = ac.createGain(); lfoGain.gain.value = 24;
  lfo.connect(lfoGain).connect(osc.frequency);
  const lp = ac.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 340; lp.Q.value = 2;
  const dist = ac.createWaveShaper(); dist.curve = _makeDistortionCurve(400);
  const g = ac.createGain();
  env(g, t0, 0.05, 0.9, 0.55, 0.55);
  osc.connect(dist).connect(lp).connect(g).connect(ac.destination);
  osc.start(t0); lfo.start(t0);
  osc.stop(t0 + 1.55); lfo.stop(t0 + 1.55);
}

function doorCreak(ac, t0) {
  // Slow scraping tone with slight pitch drift, filtered noise on top
  const osc = ac.createOscillator(); osc.type = "square"; osc.frequency.setValueAtTime(180, t0);
  osc.frequency.exponentialRampToValueAtTime(240, t0 + 0.9);
  const lp = ac.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 900;
  const g = ac.createGain();
  env(g, t0, 0.06, 0.4, 0.55, 0.14);
  osc.connect(lp).connect(g).connect(ac.destination);
  osc.start(t0); osc.stop(t0 + 1.05);

  // sprinkle a bit of noise
  const buf = ac.createBuffer(1, ac.sampleRate * 0.9, ac.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * 0.4;
  const src = ac.createBufferSource(); src.buffer = buf;
  const bp = ac.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 1200; bp.Q.value = 4;
  const ng = ac.createGain(); env(ng, t0, 0.05, 0.3, 0.55, 0.08);
  src.connect(bp).connect(ng).connect(ac.destination);
  src.start(t0); src.stop(t0 + 0.95);
}

function levelUp(ac, t0) {
  // Rising arpeggio: triad going up
  const freqs = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
  freqs.forEach((f, i) => {
    const o = ac.createOscillator(); o.type = "triangle"; o.frequency.value = f;
    const g = ac.createGain();
    const t = t0 + i * 0.08;
    env(g, t, 0.005, 0.02, 0.24, 0.18);
    o.connect(g).connect(ac.destination);
    o.start(t); o.stop(t + 0.28);
  });
}

function deathToll(ac, t0) {
  // Slow low bell-like tone with harmonic overtone
  const fundamental = 82.4; // E2
  [fundamental, fundamental * 3, fundamental * 5.4].forEach((f, i) => {
    const o = ac.createOscillator(); o.type = "sine"; o.frequency.value = f;
    const g = ac.createGain();
    const peak = [0.35, 0.14, 0.06][i];
    env(g, t0, 0.02, 0.15, 1.6, peak);
    o.connect(g).connect(ac.destination);
    o.start(t0); o.stop(t0 + 1.85);
  });
}

function hpHit(ac, t0) {
  // Sharp thud: brief low sine + high-freq noise burst
  const o = ac.createOscillator(); o.type = "sine"; o.frequency.setValueAtTime(120, t0);
  o.frequency.exponentialRampToValueAtTime(40, t0 + 0.14);
  const g = ac.createGain();
  env(g, t0, 0.003, 0.02, 0.16, 0.5);
  o.connect(g).connect(ac.destination);
  o.start(t0); o.stop(t0 + 0.2);
}

function coinPickup(ac, t0) {
  const notes = [1046.5, 1318.51]; // C6 E6
  notes.forEach((f, i) => {
    const o = ac.createOscillator(); o.type = "square"; o.frequency.value = f;
    const g = ac.createGain();
    const t = t0 + i * 0.05;
    env(g, t, 0.003, 0.02, 0.12, 0.14);
    o.connect(g).connect(ac.destination);
    o.start(t); o.stop(t + 0.16);
  });
}

function _makeDistortionCurve(amount) {
  const n = 256;
  const curve = new Float32Array(n);
  const deg = Math.PI / 180;
  for (let i = 0; i < n; i++) {
    const x = (i * 2) / n - 1;
    curve[i] = ((3 + amount) * x * 20 * deg) / (Math.PI + amount * Math.abs(x));
  }
  return curve;
}

// -------- Public API --------

const VOICES = {
  sword_clang: swordClang,
  boss_roar:   bossRoar,
  door_creak:  doorCreak,
  level_up:    levelUp,
  death_toll:  deathToll,
  hp_hit:      hpHit,
  coin:        coinPickup,
};

export function playSfx(name) {
  if (muted()) return;
  const ac = ctx();
  if (!ac) return;
  // Auto-resume on user interaction (Chrome autoplay policy)
  if (ac.state === "suspended") ac.resume().catch(() => {});
  const v = VOICES[name];
  if (!v) return;
  try { v(ac, ac.currentTime); } catch (e) { /* fail silently */ }
}

/**
 * Auto-select the right SFX based on the AI Director beat.
 * Call this once per turn after `done` event lands.
 */
export function playContextualSfxForTurn({ musicTag, isDeath, isBoss, hpDelta, goldDelta, xpDelta, leveledUp, enemyName }) {
  if (isDeath) { playSfx("death_toll"); return; }
  if (leveledUp) { playSfx("level_up"); return; }
  if (isBoss && enemyName) { playSfx("boss_roar"); return; }
  if (musicTag === "combat" && enemyName) { playSfx("sword_clang"); return; }
  if (typeof hpDelta === "number" && hpDelta < 0) { playSfx("hp_hit"); return; }
  if (typeof goldDelta === "number" && goldDelta > 0) { playSfx("coin"); return; }
  if (musicTag === "descent" || musicTag === "creepy") { playSfx("door_creak"); return; }
}
